import {Map, fromJS} from 'immutable';
import moment from 'moment';
import {Store} from 'client/framework';
import appStore from 'client/AppStore';
import sessionStore from 'client/Session/SessionStore';
import {resource} from '@albert-io/json-api-framework/request/builder';
import SelectableDateRangeStore from 'generic/SelectableDateRange/SelectableDateRange.store';

import studentPracticeExamsListActions from './StudentPracticeExamsList.actions';

class StudentPracticeExamsListStore extends Store {
  constructor(name) {
    super(name);
    this.selectableDateRangeStore = new SelectableDateRangeStore(
      'StudentPracticeExamsListSelectableDateRangeStore'
    );
    this.setInitialData(
      fromJS({
        lastValidDates: {
          startDate: null,
          endDate: null
        }
      })
    );
    this.handle(studentPracticeExamsListActions.SET_LAST_VALID_DATES, this._setLastValidDates);
  }

  _setLastValidDates({startDate, endDate}) {
    this.writeData(
      'lastValidDates',
      Map({
        startDate: startDate ? moment(startDate).startOf('day').format() : null,
        endDate: endDate ? moment(endDate).endOf('day').format() : null
      })
    );
  }

  _addStatusFiltersToQuery(query, examStatus) {
    const queryParam = appStore.routerProps.location.query.examStatus;
    const examStatusParam = examStatus !== undefined ? examStatus : queryParam;
    if (!examStatusParam) {
      // The student has not submitted
      // AND it is not past due OR the assignment allows late submissions
      return query.filter({
        'meta.student_submitted': false,
        any_of: [
          {
            due_date: {
              greater_than: 'now'
            }
          },
          {
            allow_late_submissions: true
          }
        ]
      });
      // CLOSED
    }
    if (examStatusParam === 'closed') {
      // The student has submitted
      // OR the assignment is past due AND does not allow late submissions
      return query.filter({
        any_of: [
          {
            'meta.student_submitted': true
          },
          {
            due_date: {
              less_than: 'now'
            },
            allow_late_submissions: false
          }
        ]
      });
      // ALL
    }
    return query;
  }

  getAssignmentCountsByStatus() {
    const statuses = {
      open: null,
      closed: 'closed',
      all: 'all'
    };
    return Object.entries(statuses).reduce((acc, [statusName, statusValue]) => {
      const query = this.getStudentPracticeExamsQuery(statusValue).pageSize(0);
      query.getResource();
      const assignmentCount = query.getResourceMetadata().getIn(['page', 'total_entries']);
      acc[statusName] = assignmentCount;
      return acc;
    }, {});
  }

  getStudentPracticeExamsQuery(examStatus) {
    const userId = sessionStore.getUserId();
    const query = resource('assignments_v3')
      .mandarkEndpoint(['students_v2', userId, 'assignments_v3'])
      .filter({
        assignment_type: 'exam',
        classrooms_v1: appStore.routerProps.params.classId,
        all_of: [
          {
            any_of: [
              {
                start_date: {
                  null: true
                }
              },
              {
                start_date: {
                  less_than: 'now'
                }
              }
            ]
          }
        ],
        included: {
          students_v2: {
            id: userId
          },
          student_classrooms_v1: {
            students_v2: {
              id: userId
            }
          }
        }
      })
      .filter(
        {
          due_date: {
            greater_than_inclusive: this.getLastValidDates().get('startDate'),
            less_than_inclusive: this.getLastValidDates().get('endDate')
          }
        },
        this.shouldFilterByDueDate()
      )
      .include('sections_v1')
      .include('student_classrooms_v1')
      .include('students_v2', userId)
      .customQuery({
        with_meta: 'assignment_v3,student_v2'
      })
      .meta({
        context: {
          student: {
            id: userId
          }
        }
      });
    return this._addStatusFiltersToQuery(query, examStatus);
  }

  doesClassroomHavePracticeExams() {
    const query = resource('assignments_v3')
      .mandarkEndpoint(['students_v2', sessionStore.getUserId(), 'assignments_v3'])
      .filter({
        assignment_type: 'exam',
        classrooms_v1: appStore.routerProps.params.classId,
        any_of: [
          {
            start_date: {
              null: true
            }
          },
          {
            start_date: {
              less_than: 'now'
            }
          }
        ]
      });

    return query.isResourcePopulated() && !query.getResource().isEmpty();
  }

  getStudentPracticeExams() {
    return this.getStudentPracticeExamsQuery().getResource();
  }

  shouldFilterByDueDate() {
    return this.getLastValidDates().every((date) => Boolean(date));
  }

  getSelectableDateRangeStore = () => this.selectableDateRangeStore;

  getLastValidDates = () => this.readData('lastValidDates');
}

export default new StudentPracticeExamsListStore('StudentPracticeExamsListStore');
